import { useQuery } from "@tanstack/react-query";
import type { AccountAccessPayload } from "@dealer-center/ddc-pages-configuration-interface";
import {
  CONFIGURATION_QUERY_KEY_BASE,
  configurationRequest
} from "./configurationRequest";

export interface UseGetAccountAccessOptions {
  userId?: string;
  accountId?: string;
}

/**
 * Reads the subject's account hierarchy (FR-028, FR-029) and the per-account grant state (FR-031).
 *
 * Returns `AccountAccessPayload` rather than the BFF's declared `AccountAccessResponse`: that endpoint
 * is a pure pass-through and its return type stays `Record<string, unknown>` by the sibling feature's
 * design, so the UI-facing shape is transcribed separately in `interface/` from live-verified
 * observations — not from CC9's swagger, which omits `children` from `AccountPermissionNode` even
 * though every live node carries it.
 *
 * **The slowest of the four endpoints — ~5–6s through the BFF, and over the 4s prod CC9 bound today.**
 * Two consequences: the loading state is not decorative, and the tab strip must NOT block on this
 * query (research R-007). `retry: 1` stays as-is; more retries would multiply an already-slow call.
 *
 * `writeAccess` on each node is display data about the subject and MUST NOT gate anything
 * (Monorepo Principle VII).
 */
export const useGetAccountAccess = ({
  userId,
  accountId
}: UseGetAccountAccessOptions) =>
  useQuery({
    queryKey: [
      CONFIGURATION_QUERY_KEY_BASE,
      "accountaccess",
      accountId,
      userId
    ],
    queryFn: () => {
      if (!userId || !accountId) {
        throw new Error(
          "useGetAccountAccess requires both userId and accountId"
        );
      }

      return configurationRequest<AccountAccessPayload>({
        resource: "accountaccess",
        userId,
        accountId
      });
    },
    enabled: Boolean(userId && accountId),
    retry: 1,
    staleTime: 1000 * 60 * 5
  });
